<script setup lang="ts">
import { scaleBand } from 'd3';
import type { LineChartOptions } from '../index.vue';
import { useElementBounding } from '@vueuse/core';

// ================================================ Props
const bottomMargin = defineModel<number>({ required: true, default: 8 });
const props = defineProps<{
  loading: boolean;
  keys: string[];
  options: LineChartOptions;
  containerWidth: number;
}>();

// ================================================ Data
const xAxisValues = ref<HTMLDivElement[]>([]);

// ================================================ Computed
const xScale = computed(() => scaleBand().domain(props.keys).range([0, 100]).padding(0.4));

// ================================================ Methods
const updateBottomMargin = (value: HTMLElement[]) => {
  if (!value.length) return 0;
  const values = value.map((value) => {
    const { height } = useElementBounding(value);
    return height.value;
  });
  bottomMargin.value = Math.max(...values) + 8;
};

const shouldShowLabel = (index: number) => {
  const totalLabels = props.keys.length;
  if (totalLabels === 0) return false;

  // Estimer la largeur moyenne d'un label en pixels (basé sur la longueur du texte)
  const avgLabelLength = props.keys.reduce((sum, key) => sum + key.length, 0) / totalLabels;
  const estimatedLabelWidth = avgLabelLength * 2; // ~6px par caractère pour text-xs
  const minSpacing = 8; // Espacement minimal entre les labels
  const requiredSpacePerLabel = estimatedLabelWidth + minSpacing;

  // Calculer combien de labels peuvent tenir
  const maxVisibleLabels = Math.floor(props.containerWidth / requiredSpacePerLabel);

  // Si on peut afficher tous les labels, les afficher
  if (maxVisibleLabels >= totalLabels) {
    return true;
  }

  // Sinon, calculer le facteur de saut pour afficher au maximum maxVisibleLabels
  const skipFactor = Math.max(1, Math.ceil(totalLabels / maxVisibleLabels));

  return index % skipFactor === 0;
};

// ================================================ Watch
watch(
  () => xAxisValues.value,
  () => {
    updateBottomMargin(xAxisValues.value);
  },
  { deep: true, immediate: true },
);
</script>
<template>
  <div
    v-for="(entry, i) in keys"
    ref="xAxisValues"
    :key="i"
    :style="{
      left: `${(xScale(entry) ?? 0) + xScale.bandwidth() / 2}%`,
      top: '100%',
      transform: 'rotate(45deg) translateX(4px) translateY(8px)',
      display: shouldShowLabel(i) ? 'block' : 'none',
    }"
    class="absolute overflow-visible text-gray-400 mt-2"
  >
    <div ref="xAxisValues" class="absolute text-xs -translate-y-1/2 whitespace-pre">{{ entry }}</div>
  </div>
</template>
